import { useMemo, type ReactNode, type FC } from 'react'
import { type AccessControlConfig } from '../../../legacy/components/AccessControl/accessControl.utils'
import { AccessControlsContext } from './AccessControlsContext'
import { useOptionalAccessControlsContext } from './AccessControls.hooks'

// Widened so domains using their own enum types (e.g. local FeatureEntitlements) can pass
// their access controls through; runtime evaluation only inspects string slugs.
type AnyAccessControlConfig = AccessControlConfig<any, any, any, any, any>

export interface AdditionalAccessControlsProviderProps {
  /**
   * Extra access control configs to AND with the surrounding route's accumulated
   * access controls. Each entry follows the standard {@link AccessControlConfig}
   * AND/OR semantics: a single config is required, an array of configs is OR'd.
   */
  additionalAccessControls: (AnyAccessControlConfig | AnyAccessControlConfig[])[]
  children?: ReactNode
}

/**
 * Pushes additional access control entries onto the existing
 * {@link AccessControlsContext} for a sub-tree without modifying the surrounding
 * route's static `accessControl` config.
 *
 * The `MultiScopePicker` reads from this context to grey out warehouses that
 * don't satisfy every accumulated entry; tab-level UI can use this provider to
 * tighten the required access while a particular tab is active.
 */
export const AdditionalAccessControlsProvider: FC<AdditionalAccessControlsProviderProps> = ({
  additionalAccessControls,
  children,
}) => {
  const parent = useOptionalAccessControlsContext()

  const value = useMemo(
    () => ({
      accessControls: [...(parent?.accessControls ?? []), ...additionalAccessControls],
    }),
    [parent, additionalAccessControls]
  )

  return <AccessControlsContext.Provider value={value}>{children}</AccessControlsContext.Provider>
}
